import React, { useState } from 'react';
import axios from 'axios';
import { MailCheck, Loader2, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Shown right after a successful customer/investor signup.
 * Tells the user to check their inbox and offers a one-click resend.
 */
const RegistrationSuccessView = ({ email, accountType, onBackToSignIn }) => {
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleResend = async () => {
    setSending(true);
    try {
      await axios.post(`${API}/auth/resend-verification`, { email });
      setSent(true);
      toast.success('Verification email re-sent.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not resend email');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-16 px-4" data-testid="registration-success-view">
      <Card className="max-w-md mx-auto">
        <CardHeader className="text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <MailCheck className="h-8 w-8 text-green-600" />
          </div>
          <CardTitle className="text-2xl">Check your inbox</CardTitle>
          <p className="text-gray-500 text-sm mt-2">
            We&apos;ve sent a verification link to
          </p>
          <p className="font-medium text-gray-900 mt-1" data-testid="registered-email-display">
            {email}
          </p>
        </CardHeader>

        <CardContent className="space-y-4">
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900">
            <p className="font-medium mb-1">Next step</p>
            <p>
              Click the link in the email within <strong>15 minutes</strong> to activate your{' '}
              {accountType === 'investor' ? 'investor' : 'customer'} account. After verifying, sign in
              to access your dashboard.
            </p>
          </div>

          <Button
            variant="outline"
            className="w-full"
            onClick={handleResend}
            disabled={sending || sent}
            data-testid="signup-resend-btn"
          >
            {sending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            {sent && <CheckCircle2 className="h-4 w-4 mr-2 text-green-600" />}
            {sent ? 'Email re-sent' : sending ? 'Sending…' : "Didn't get it? Resend email"}
          </Button>

          <Button
            variant="ghost"
            className="w-full text-green-700 hover:text-green-800"
            onClick={onBackToSignIn}
            data-testid="signup-back-to-signin-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to sign in
          </Button>

          <p className="text-xs text-gray-500 text-center">
            Can&apos;t find the email? Check your spam folder or contact{' '}
            <a href="mailto:support@dayefarmltd.com" className="text-green-700 hover:underline">
              support@dayefarmltd.com
            </a>
            .
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default RegistrationSuccessView;
